import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useState, useEffect } from "react";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import { BrandProvider } from "./context/BrandContext";
import { NotificationProvider } from "./context/NotificationContext";

// Global axios config — send httpOnly cookies on every request, add CSRF header
axios.defaults.withCredentials = true;
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

// Global response interceptor — surface friendly toasts for known timeout
// failures so heavy LLM endpoints never silently show a generic error.
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail;

    // 408 = our send_with_timeout wrapper fired (LLM took longer than 55s)
    if (status === 408) {
      toast.error("AI took too long to respond", {
        description:
          typeof detail === "string"
            ? detail
            : "Try again or narrow the scope of the request. For heavy generations, the async endpoint runs in the background.",
        duration: 8000,
      });
    }
    return Promise.reject(error);
  }
);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
import Layout from "./components/Layout";
import Dashboard from "./pages/Dashboard";
import Competitors from "./pages/Competitors";
import Intelligence from "./pages/Intelligence";
import ContentStudio from "./pages/ContentStudio";
import VideoStudio from "./pages/VideoStudio";
import Learning from "./pages/Learning";
import Settings from "./pages/Settings";
import SocialMedia from "./pages/SocialMedia";
import Login from "./pages/Login";
import AdminSettings from "./pages/AdminSettings";
import Battlecards from "./pages/Battlecards";
import TrendsAnalytics from "./pages/TrendsAnalytics";
import MarketPosition from "./pages/MarketPosition";
import SentimentAnalysis from "./pages/SentimentAnalysis";
import WorkflowAutomation from "./pages/WorkflowAutomation";
import PredictiveAI from "./pages/PredictiveAI";
import AlgorithmicIntelligence from "./pages/AlgorithmicIntelligence";
import CrossBrandMomentum from "./pages/CrossBrandMomentum";
import "./App.css";

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Validate session via httpOnly cookie (preferred) or legacy Bearer token (migration path)
    const validateSession = async () => {
      // Migration path: if old Bearer token exists in localStorage, send it once so /me works,
      // then remove it (cookie should now be set by backend on subsequent login).
      const legacyToken = localStorage.getItem("token");
      if (legacyToken) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${legacyToken}`;
      }

      try {
        const res = await axios.get(`${API}/auth/me`);
        setUser(res.data);
      } catch (_e) {
        // No valid session — clear any stale data
        localStorage.removeItem("token");
        localStorage.removeItem("user"); // legacy key — older builds cached user JSON here
        delete axios.defaults.headers.common['Authorization'];
        setUser(null);
      } finally {
        // Once the cookie flow is established we no longer need the legacy header
        if (legacyToken) {
          localStorage.removeItem("token");
          delete axios.defaults.headers.common['Authorization'];
        }
        setLoading(false);
      }
    };
    validateSession();
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
    // Persist only non-sensitive user info — token lives in httpOnly cookie
    localStorage.setItem("user", JSON.stringify(userData));
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (_e) {
      // ignore — we still want to clear local state
    }
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0d1117] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-[#DC143C]"></div>
      </div>
    );
  }

  return (
    <BrandProvider user={user}>
      <NotificationProvider>
        <div className="App min-h-screen">
          <BrowserRouter>
            <Routes>
              {/* Public Login Route */}
              <Route 
                path="/login" 
                element={user ? <Navigate to="/" /> : <Login onLogin={handleLogin} />} 
              />
              
              {/* Protected Routes */}
              <Route path="/" element={<Layout user={user} onLogout={handleLogout} />}>
                <Route index element={<Dashboard />} />
                <Route path="competitors" element={<Competitors />} />
                <Route path="intelligence" element={<Intelligence />} />
                <Route path="social" element={<SocialMedia />} />
                <Route path="content" element={<ContentStudio />} />
                <Route path="videos" element={<VideoStudio />} />
                <Route path="learning" element={<Learning />} />
                <Route path="battlecards" element={<Battlecards />} />
                <Route path="trends" element={<TrendsAnalytics />} />
                <Route path="market-position" element={<MarketPosition />} />
                <Route path="sentiment" element={<SentimentAnalysis />} />
                <Route path="automation" element={<WorkflowAutomation />} />
                <Route path="predictive" element={<PredictiveAI />} />
                <Route path="algorithmic" element={<AlgorithmicIntelligence />} />
                <Route path="cross-brand" element={<CrossBrandMomentum />} />
                <Route path="settings" element={<Settings />} />
                <Route path="admin" element={<AdminSettings />} />
              </Route>
            </Routes>
          </BrowserRouter>
          <Toaster position="top-right" richColors />
        </div>
      </NotificationProvider>
    </BrandProvider>
  );
}

export default App;
